import { personDisplayName } from '@carsfinders/contracts';

import { ApiError } from '../../lib/api';
import { AUCTION_URL, LOCAL_APP_URL } from '../../lib/env';
import { t } from '../../lib/i18n';
import {
  type AdminBid,
  BidStatus,
  type RelayStatus,
  type SlaSeverity,
  type WinnerReplacementConfirmation,
} from './types';

/**
 * The lot's page on the auction site (`/cars/<listing_id>`), in a new tab —
 * the same URL the auction's own «Мої ставки» and won-cars rows link to.
 *
 * Owner decision 2026-10-18: the admin's own lot page (`/lots/$id`) is gone; a
 * lot name anywhere in the admin opens the car on the site, and the lot's bids
 * live on the bid page instead. A lot whose car document is gone has no
 * listing id; its Mongo `_id` is accepted by the same route.
 */
export function lotSiteHref(lot: { carId: string; listingId?: string | null }): string {
  return `${AUCTION_URL}/cars/${encodeURIComponent(lot.listingId || lot.carId)}`;
}

/**
 * A local-ledger card (local-ledger bridge) on local.cars-finders.com — what
 * «Відкрити в обліку» opens, in a new tab, under the ledger's own session.
 */
export function localVehicleHref(vehicleId: string): string {
  return `${LOCAL_APP_URL}/vehicles/${encodeURIComponent(vehicleId)}`;
}

/**
 * Self-contained helpers for the admin bids feature — pure stat aggregation
 * (ported from the web AdminBidStatusPanel `bidStats` useMemo) plus display
 * formatters. No React, no network — unit-tested in utils.test.ts.
 */

/**
 * Narrow a rejected mutation to the winner-replacement handshake payload.
 *
 * api-node answers `PATCH /bids/:id/status` with 409 + `requiresConfirmation`
 * when the lot already has a winner; the operator must confirm before the call
 * is re-issued with `forceReplace: true`. This lived inside the detail page
 * until Design D put the same «Виграно» button on every table row — a 409 that
 * surfaces there as a generic "не вдалося" would leave the operator unable to
 * replace a winner from the screen that now offers the action.
 */
export function asWinnerConfirmation(error: unknown): WinnerReplacementConfirmation | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null;
  const body = error.body as Partial<WinnerReplacementConfirmation> | undefined;
  if (!body?.requiresConfirmation) return null;
  return {
    requiresConfirmation: true,
    message: body.message ?? '',
    existingWinners: body.existingWinners ?? [],
  };
}

/** Join truthy class names (stand-in for the web `cn`). */
export function cn(...classes: Array<string | false | null | undefined>): string {
  return classes.filter(Boolean).join(' ');
}

/** Format an integer amount as `CHF 12 500` (spaces for thousands, web parity). */
export function formatChf(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return 'CHF 0';
  return `CHF ${new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
    .format(Number(value))
    .replace(/,/g, ' ')}`;
}

/** Short date `dd/mm/yyyy` or an em dash when absent. */
export function formatShortDate(date: string | null | undefined): string {
  if (!date) return '—';
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/** Bidder label from a populated user (display name → full name → email → id). */
export function bidderLabel(bid: Pick<AdminBid, 'user' | 'userId'>): string {
  // «First Last» before the chosen handle — see personDisplayName.
  return personDisplayName(bid.user) || bid.userId;
}

/**
 * True when `bidderLabel` fell all the way back to the raw email — i.e. the
 * client has no display name or first/last name on file. Callers use this to
 * avoid printing the same email twice (once as the bold identity line, again
 * as its own "email" subline).
 */
export function bidderIsEmail(bid: Pick<AdminBid, 'user' | 'userId'>, bidder: string): boolean {
  return Boolean(bid.user?.email) && bidder === bid.user?.email;
}

export interface BidStats {
  total: number;
  active: number;
  won: number;
  outbid: number;
  /** Sum of `bid` over the currently-active bids only (web parity). */
  totalActiveValue: number;
}

/**
 * Aggregate list-level counters from a bid array (port of the web
 * AdminBidStatusPanel stats: total value counts active bids only).
 */
export function computeBidStats(bids: AdminBid[]): BidStats {
  return bids.reduce<BidStats>(
    (stats, bid) => {
      stats.total += 1;
      if (bid.isActive) stats.totalActiveValue += bid.bid;
      switch (bid.status) {
        case BidStatus.Active:
          stats.active += 1;
          break;
        case BidStatus.Won:
          stats.won += 1;
          break;
        case BidStatus.Outbid:
          stats.outbid += 1;
          break;
      }
      return stats;
    },
    { total: 0, active: 0, won: 0, outbid: 0, totalActiveValue: 0 },
  );
}

/** Which of the two `/bids` archive tabs a bid belongs in. */
export type BidArchiveTab = 'active' | 'archive';

/**
 * "Archived" = a terminal auction status (`Outbid | Won | Cancelled`) —
 * everything except `Active` ("Under Review"). Deliberately NOT derived from
 * the lot's closing date: on live data 4/20 bids are `Active` past their
 * `auction_end_date` (the open closing-time-offset defect — the countdown
 * gates print a raw end date while other surfaces use one shifted +2h). Those
 * bids are still awaiting an operator decision and must stay in the ACTIVE
 * tab; hiding them in the archive because the clock looks closed would bury
 * exactly the ones that most need attention.
 */
export function bidArchiveTab(bid: Pick<AdminBid, 'status'>): BidArchiveTab {
  return bid.status === BidStatus.Active ? 'active' : 'archive';
}

/** i18n key for an SLA severity dot label (bids.relayQueue.sla.*). */
export function slaSeverityTranslationKey(severity: SlaSeverity): string {
  return `bids.relayQueue.sla.${severity}`;
}

/**
 * Dot colour for an SLA severity, on the Design D ramp.
 *
 * These were raw Tailwind (`bg-green-500`) until the `Redesign-D-Admin`
 * artboards landed. Two greens in one product is exactly the divergence the
 * `cf-` tokens exist to prevent, so the dot now reads from the same three
 * tokens the relay chips and the dashboard SLA badge use.
 */
export function slaSeverityColorClass(severity: SlaSeverity): string {
  switch (severity) {
    case 'green':
      return 'bg-cf-success';
    case 'amber':
      return 'bg-cf-warn';
    case 'red':
      return 'bg-cf-danger';
    default:
      return 'bg-cf-ink-faint';
  }
}

/** Foreground for an SLA severity — the artboard sets the value, not only a dot. */
export function slaSeverityTextClass(severity: SlaSeverity): string {
  switch (severity) {
    case 'green':
      return 'text-cf-success';
    case 'amber':
      return 'text-cf-warn';
    case 'red':
      return 'text-cf-danger';
    default:
      return 'text-cf-ink-subtle';
  }
}

/** i18n key for a relay status badge (reuse the shared bids.relayStatus.*). */
export function relayStatusTranslationKey(status: RelayStatus): string {
  return `bids.relayStatus.${status}`;
}

/**
 * Chip colours per auction status, from the artboard's `ST` table.
 *
 * Deliberately the same four pairs `apps/auction` gives the client-facing bid
 * rows: one design system across two applications, so "перебито" is the same
 * red on the operator's screen as on the bidder's.
 */
export const BID_STATUS_CHIP: Record<BidStatus, string> = {
  [BidStatus.Active]: 'bg-cf-warn-tint text-cf-warn',
  [BidStatus.Outbid]: 'bg-cf-danger-tint text-cf-danger',
  [BidStatus.Won]: 'bg-cf-success-tint text-cf-success',
  [BidStatus.Cancelled]: 'bg-cf-fill text-cf-ink-subtle',
};

/**
 * Design D status labels — `bids.d.state.*`, the wording the artboards print
 * ("На розгляді / Перебито / Виграно / Не виграно"), not the older
 * `tabs.bids.*` set whose "Не Виграно" capitalises mid-phrase.
 */
export const BID_STATUS_D_LABEL_KEYS: Record<BidStatus, string> = {
  [BidStatus.Active]: 'bids.d.state.underReview',
  [BidStatus.Outbid]: 'bids.d.state.outbid',
  [BidStatus.Won]: 'bids.d.state.won',
  [BidStatus.Cancelled]: 'bids.d.state.lost',
};

/** Chip colours per relay pipeline state (same ramp, logistics blue mid-flow). */
export const RELAY_STATUS_CHIP: Record<RelayStatus, string> = {
  captured: 'bg-cf-fill text-cf-ink-muted',
  queued: 'bg-cf-info-tint text-cf-info-ink',
  relayed: 'bg-cf-info-tint text-cf-info-ink',
  won: 'bg-cf-success-tint text-cf-success',
  lost: 'bg-cf-danger-tint text-cf-danger',
  cancelled: 'bg-cf-fill text-cf-ink-subtle',
  expired: 'bg-cf-fill text-cf-ink-subtle',
  // Phase 13: the house bounced it — a real failure the operator must read.
  upstream_rejected: 'bg-cf-danger-tint text-cf-danger',
};

/** Amount without the currency word — the artboard prints `CHF` separately. */
export function formatAmount(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '0';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
    .format(Number(value))
    .replace(/,/g, ' ');
}

/** `dd.mm` + `yyyy` split the way the artboard's date column stacks them. */
export function formatDayMonth(date: string | null | undefined): string {
  if (!date) return '—';
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return '—';
  return new Intl.DateTimeFormat('uk-UA', { day: '2-digit', month: '2-digit' }).format(parsed);
}

/** Clock component of a timestamp, or an empty string when there is none. */
export function formatClock(date: string | null | undefined): string {
  if (!date) return '';
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return '';
  return new Intl.DateTimeFormat('uk-UA', { hour: '2-digit', minute: '2-digit' }).format(parsed);
}

/** Under this much time left, the auction-end caption turns amber. */
const AUCTION_END_SOON_MS = 24 * 60 * 60 * 1000;

export interface AuctionEnd {
  /** «завершується 13.10, 14:00» / «аукціон завершився 12.10, 14:00». */
  label: string;
  tone: 'open' | 'soon' | 'ended';
}

/**
 * True while the lot's real auction is still running — before then the
 * operator cannot know the outcome, so «Виграно / Не виграно» stay disabled
 * (owner, 2026-10-11). An unknown or unparseable end reads as closed: such a
 * bid must still be decidable.
 */
export function isAuctionRunning(endsAt: string | null | undefined, now: number = Date.now()): boolean {
  if (!endsAt) return false;
  const end = new Date(endsAt).getTime();
  return Number.isFinite(end) && end > now;
}

/**
 * The lot's REAL auction close as the operator reads it beside a bid (owner,
 * 2026-10-12: «було б добре якби адмін бачив коли завершується ставка») — the
 * deadline the bid must be relayed before. No customer offset: the admin sees
 * the real end, like the auction schedule. Null for a lot without an end.
 */
export function auctionEndOf(
  endsAt: string | Date | null | undefined,
  now: number = Date.now(),
): AuctionEnd | null {
  if (!endsAt) return null;
  const iso = endsAt instanceof Date ? endsAt.toISOString() : endsAt;
  const end = new Date(iso).getTime();
  if (Number.isNaN(end)) return null;
  const date = `${formatDayMonth(iso)}, ${formatClock(iso)}`;
  if (end <= now) return { label: t('admin.bids.auctionEnd.ended', { date }), tone: 'ended' };
  return {
    label: t('admin.bids.auctionEnd.endsAt', { date }),
    tone: end - now < AUCTION_END_SOON_MS ? 'soon' : 'open',
  };
}

/** Caption colour per {@link AuctionEnd} tone. */
export const AUCTION_END_TONE_CLASS: Record<AuctionEnd['tone'], string> = {
  open: 'text-cf-ink-muted',
  soon: 'font-semibold text-cf-warn',
  ended: 'text-cf-ink-subtle',
};
